// @flow
export const TABLE_HEADER_STYLE = {
  outline: 'none',
  display: 'flex',
  lineHeight: 1,
};

export type SortDirectionType = 'ASC' | 'DESC';

type Index = {
  index: number,
};

export type Item = Object; // Must contain an id or uuid or catalogId

// opaque means you can't see the structure of the type from outside this file.
export opaque type SelectedItem = Item & Index;

export opaque type SelectionState = {
  lastSelectedItem?: SelectedItem,
  selectedItems: SelectedItem[],
};

type SelectionEvent = {
  ctrlKey?: boolean,
  metaKey?: boolean,
  shiftKey?: boolean,
  altKey?: boolean,
  keyCode?: number,
};

export function createMockSelectionState(
  selectedItems: Object[],
  lastSelectedItem: Object
): SelectionState {
  return {
    selectedItems,
    lastSelectedItem,
  };
}

export function getNewSelectionState(): SelectionState {
  return {
    lastSelectedItem: undefined,
    selectedItems: [],
  };
}

function getItemId(item: Item): ?string {
  return item.id || item.uuid || item.catalogId; // Any other id types?
}

export function getIndexOfFirstSelectedItem(selectionState: SelectionState) {
  return (
    selectionState.selectedItems[0] && selectionState.selectedItems[0].index
  );
}

export function getSelectedIndices(selectionState: SelectionState): number[] {
  return selectionState.selectedItems.map(item => item.index);
}

export function isItemWithIdSelected(
  id: ?string,
  selectionState: SelectionState
) {
  return (
    selectionState.selectedItems.findIndex(el => getItemId(el) === id) !== -1
  );
}

export function isItemAtIndexSelected(
  index: ?number,
  selectionState: SelectionState
) {
  return (
    selectionState.selectedItems.findIndex(el => el.index === index) !== -1
  );
}

export function findItemIndex(id: ?string, sortedItems: Item[]) {
  let selectedIndex;
  if (id) {
    selectedIndex = sortedItems.findIndex(item => getItemId(item) === id);
  }

  return selectedIndex;
}

export function reindexSelectedItems(
  sortedItems: Item[],
  selectionState: SelectionState
): SelectionState {
  return {
    ...selectionState,
    selectedItems: selectionState.selectedItems.reduce((acc, item) => {
      const index = findItemIndex(getItemId(item), sortedItems);
      if (index !== undefined && index > -1) {
        acc.push(sortedItems[index]);
      }
      return acc;
    }, []),
  };
}

function getNewSelectedItemForItem(src: Item, index: number): SelectedItem {
  if (src.id) {
    return { id: src.id, index };
  } else if (src.uuid) {
    return { uuid: src.uuid, index };
  } else if (src.catalogId) {
    return { catalogId: src.catalogId, index };
  } else {
    return { id: 0, index };
  }
}

export function computeNewSelection(
  index: number,
  sortedItems: Item[],
  selectionState: SelectionState,
  event: SelectionEvent,
  singleSelectionOnly: boolean = false
): SelectionState {
  if (!singleSelectionOnly && event && (event.metaKey || event.ctrlKey)) {
    // Command/Ctrl click on a selected item deselects it.
    if (isItemAtIndexSelected(index, selectionState)) {
      return {
        ...selectionState,
        selectedItems: selectionState.selectedItems.filter(
          el => el.index !== index
        ),
      };
    } else {
      // Otherwise select it
      const selectedItem = getNewSelectedItemForItem(sortedItems[index], index);
      if (selectedItem) {
        return {
          lastSelectedItem: selectedItem,
          selectedItems: [...selectionState.selectedItems, selectedItem],
        };
      }
    }
  } else if (!singleSelectionOnly && event && event.shiftKey) {
    function makeRange(size: number, startAt = 0): number[] {
      return [...Array(size).keys()].map(i => i + startAt);
    }

    const { lastSelectedItem, selectedItems } = selectionState;

    const newLastSelectedItem =
      lastSelectedItem &&
      isItemAtIndexSelected(lastSelectedItem.index, selectionState)
        ? lastSelectedItem
        : getNewSelectedItemForItem(sortedItems[0], 0);
    if (newLastSelectedItem && newLastSelectedItem.index !== undefined) {
      return {
        ...selectionState,
        selectedItems: selectedItems.concat(
          makeRange(
            Math.abs(newLastSelectedItem.index - index) + 1,
            Math.min(newLastSelectedItem.index, index)
          )
            .map(i => getNewSelectedItemForItem(sortedItems[i], i))
            .filter(i => !isItemAtIndexSelected(i.index, selectionState))
        ),
      }; // Remove dups
    }
  } else {
    // Normal selection, deselect everything else
    if (sortedItems[index]) {
      const newSelectedItem = getNewSelectedItemForItem(
        sortedItems[index],
        index
      );
      if (newSelectedItem) {
        return {
          selectedItems: [newSelectedItem],
          lastSelectedItem: newSelectedItem,
        };
      }
    }
  }

  return selectionState;
}



// WEBPACK FOOTER //
// ./src/util/tableUtils.js